import styled from 'styled-components';
import { memo } from 'react';
import Spinners from '../../components/spinner';
import Heading from '../../components/typography';
import HtmlPreviewer from '../../components/rich-text-editor/htmlPreviewer';
import { PopoverContentWrapper } from '../../components/modal/modal.styled';
import { useWealthlaneNotificationApi } from '../../hooks';
import useGetEmailTemplateById from '../../hooks/get/useGetEmailTemplatesbyId';

// --- Styled Components ---
const PopoverBody = styled.div`
  box-sizing: border-box; 
  width: 40vw;  
  max-width: 850px;
  padding: 16px;
`;

const CenteredContainer = styled.div`
  padding: 16px;
  text-align: center;
  min-height: 120px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const StyledHtmlPreviewer = styled(HtmlPreviewer)`
  min-height: unset;
`;

// --- Sub-components ---
const Loading = () => (
  <CenteredContainer>
    <Spinners size="md" />
  </CenteredContainer>
);

const NoData = () => (
  <CenteredContainer>
    <Heading variant="body-s" title="No template available" />
  </CenteredContainer>
);

const TemplateContent = memo(
  ({ html, title }: { html: string; title: string }) => (
    <PopoverBody>
      <Heading
        variant="h4"
        title={title || '-'}
        style={{
          marginBottom: '12px',
          paddingBottom: '8px',
          borderBottom: '1px solid #eee',
        }}
      />
      <StyledHtmlPreviewer html={html} />
    </PopoverBody>
  )
);

// --- Types ---
interface EmailTemplatePopoverContentProps {
  id: string | null;
  loading?: boolean;
}

// --- Main Component ---
const EmailTemplatePopoverContent = ({
  id,
  loading = false,
}: EmailTemplatePopoverContentProps): JSX.Element => {
  const { handleRequest } = useWealthlaneNotificationApi();

  const { data, isLoading } = useGetEmailTemplateById(
    handleRequest,
    id,
  );

  const showLoading = loading || isLoading;

  // Helper function to determine what to render inside the wrapper
  const renderContent = () => {
    if (showLoading) return <Loading />;
    if (!data) return <NoData />;

    // Optional chaining removed because data is guaranteed to exist here
    return (
      <TemplateContent
        html={data.message ?? ''}
        title={data.notificationType ?? ''}
      />
    );
  };

  return (
    <PopoverContentWrapper className="roles-popovers email-template-popover">
      {renderContent()}
    </PopoverContentWrapper>
  );
};

export default memo(EmailTemplatePopoverContent);